import { useQuery } from '@tanstack/react-query';
import type {
  ReleaseScheduleListItem,
  ReleaseScheduleSourceEnum,
  ReleaseScheduleStatusEnum,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { ReleaseSort } from '@/stores/useReleaseScheduleFiltersStore';

export interface ReleaseSchedulesQueryParams {
  page?: number;
  size?: number;
  sort?: ReleaseSort;
  vendorId?: string | null;
  artistId?: string | null;
  status?: ReadonlyArray<ReleaseScheduleStatusEnum>;
  source?: ReleaseScheduleSourceEnum | null;
  from?: string | null;
  to?: string | null;
  keyword?: string;
}

export interface ReleaseSchedulesPage {
  items: ReadonlyArray<ReleaseScheduleListItem>;
  meta: ApiMeta;
}

interface AdminQuery {
  page?: number;
  size?: number;
  sort?: ReleaseSort;
  vendorId?: string;
  artistId?: string;
  status?: ReleaseScheduleStatusEnum[];
  source?: ReleaseScheduleSourceEnum;
  from?: string;
  to?: string;
  keyword?: string;
}

const cleanQuery = (p: ReleaseSchedulesQueryParams): AdminQuery => {
  const out: AdminQuery = {};
  // FE 1-based → BE 0-based
  out.page = p.page != null ? Math.max(0, p.page - 1) : 0;
  if (p.size) out.size = p.size;
  if (p.sort) out.sort = p.sort;
  if (p.vendorId) out.vendorId = p.vendorId;
  if (p.artistId) out.artistId = p.artistId;
  if (p.status && p.status.length > 0) out.status = [...p.status];
  if (p.source) out.source = p.source;
  if (p.from) out.from = p.from;
  if (p.to) out.to = p.to;
  if (p.keyword && p.keyword.trim().length > 0) out.keyword = p.keyword.trim();
  return out;
};

export const releaseSchedulesQueryKey = (p: ReleaseSchedulesQueryParams) =>
  ['admin', 'release-schedules', p] as const;

export const useReleaseSchedulesQuery = (p: ReleaseSchedulesQueryParams) =>
  useQuery<ReleaseSchedulesPage>({
    queryKey: releaseSchedulesQueryKey(p),
    queryFn: async () => {
      const [data, meta] = await apiClient.request(
        'get',
        '/admin/v1/release-schedules',
        { params: { query: cleanQuery(p) } },
      );
      const payload = data as { items?: ReleaseScheduleListItem[] | null };
      return { items: payload.items ?? [], meta };
    },
  });
